import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { CredentialVerification } from "../types";
import { formatCredentialTimestamp, formatDate } from "../lib/format-date";
import { VerificationSourceLink } from "./verification-source-link";

type RegistryReport = NonNullable<CredentialVerification["registryReport"]>;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})$/;
const ISO_DATE_RANGE = /^(\d{4}-\d{2}-\d{2})\s*[–—-]\s*(\d{4}-\d{2}-\d{2})$/;

/** A detail value as people read it: sources report dates and times in ISO
 * form, which the report shows as dates and local times. */
function displayValue(value: string): string {
  if (ISO_DATE.test(value)) {
    return formatDate(value);
  }
  if (ISO_TIMESTAMP.test(value)) {
    return formatCredentialTimestamp(value);
  }
  const range = ISO_DATE_RANGE.exec(value);
  if (range) {
    return `${formatDate(range[1])} – ${formatDate(range[2])}`;
  }
  return value;
}

/** A registry result's facts. `showVerdict` is off where the result's verdict
 * is already stated above it, such as a run report item's row and banner, so
 * it is not repeated. */
export function RegistryReportDetails({
  report,
  showVerdict = true,
}: {
  report: RegistryReport;
  showVerdict?: boolean;
}) {
  return (
    <div className="space-y-4">
      {showVerdict && (
        <div className="space-y-1">
          <p className="text-sm font-semibold">{report.label}</p>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {report.description}
          </p>
        </div>
      )}
      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        {report.details.map((detail, index) => (
          <div key={`${detail.label}-${index}`} className="min-w-0 space-y-1">
            <dt className="text-xs text-muted-foreground">{detail.label}</dt>
            <dd className="break-words">{displayValue(detail.value)}</dd>
          </div>
        ))}
      </dl>
      {report.limitations.length > 0 && (
        <div className="space-y-1 text-sm text-amber-900">
          <p className="font-medium">Limitations</p>
          <ul className="list-disc space-y-1 pl-4">
            {report.limitations.map((limitation) => (
              <li key={limitation}>{limitation}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function RegistryVerificationDialog({
  verification,
  isDownloading,
  onDownload,
  onClose,
}: {
  verification: CredentialVerification;
  isDownloading: boolean;
  onDownload: () => void;
  onClose: () => void;
}) {
  const report = verification.registryReport;
  if (!report) {
    return null;
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[85dvh] max-w-2xl overflow-y-auto">
        <DialogHeader className="text-left">
          <DialogTitle>{report.title}</DialogTitle>
          <DialogDescription>
            {verification.source} ·{" "}
            {formatCredentialTimestamp(verification.verifiedAt)}
          </DialogDescription>
        </DialogHeader>
        <RegistryReportDetails report={report} />
        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <VerificationSourceLink sourceUrl={verification.sourceUrl} />
          {verification.evidence && (
            <Button
              variant="outline"
              size="sm"
              disabled={isDownloading}
              onClick={onDownload}
            >
              <Download className="mr-2 size-4" aria-hidden="true" />
              {isDownloading ? "Downloading…" : "Download source data"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
